import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../../auth/firebase';
import { useAuthStore } from '../../auth/state';
import { FaProjectDiagram, FaBookReader } from 'react-icons/fa';
import { MdLightMode, MdDarkMode } from 'react-icons/md';  // # Import theme icons
import { doc, updateDoc } from 'firebase/firestore';  // # Import for updating theme
import './admin_dashboard.css';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import AdminDashboardUX from '../../components/ux_extra/main_pages/adm_dashboard_ux';
import VerificationModal from '../../auth/verify/verif_modal';
import cacheManager from '../../auth/cache_manager';  // # Import cache manager

function AdminDashboard() {
  const navigate = useNavigate();
  const { user, userProfile, userDashboard, resetAuth, emailVerified } = useAuthStore();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Add dark mode state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Add ref for tracking last profile access log
  const lastProfileAccessRef = React.useRef({
    timestamp: 0,
    logged: false
  });

  // # Check if we should show verification reminder
  useEffect(() => {
    if (user && !emailVerified) {
      // # Check if reminder is suppressed
      const verificationReminder = localStorage.getItem('verificationReminder');
      
      if (verificationReminder) {
        const reminderData = JSON.parse(verificationReminder);
        const now = Date.now();
        
        // # If reminder time has passed, show modal
        if (now > reminderData.nextReminder) {
          setShowVerificationModal(true);
          // # Clear old reminder
          localStorage.removeItem('verificationReminder');
        }
      } else {
        // # No reminder set, show modal
        setShowVerificationModal(true);
      }
    }
  }, [user, emailVerified]);

  // # Handle mentorship navigation
  const handleMentorshipNavigation = (path) => {
    // # Set navigation source to admin_dashboard
    sessionStorage.setItem('menteeNavigationSource', 'admin_dashboard');
    
    // # Clear any existing mentee ID to ensure admin sees their own content
    sessionStorage.removeItem('currentViewingMenteeId');
    
    // # Navigate to the mentorship page
    navigate(path);
  };

  // # Admin dashboard sections
  const dashboardSections = {
    administration: {
      title: 'Administration',
      items: [
        {
          title: 'Users',
          icon: '/icons/dash/administration.jpg',
          description: 'Advanced user management tools',
          path: '/admin/users'
        },
        {
          title: 'Subscriptions',
          icon: '/icons/dash/subscriptions.png',
          description: 'Manage subscription plans and settings',
          path: '/admin/subscriptions'
        },
        {
          title: 'Leader Boards',
          icon: '/icons/dash/leaderboard.png',
          description: 'View and manage platform leaderboards',
          path: '/admin/leaderboards'
        },
        {
          title: 'Analytics',
          icon: '/icons/dash/analytics.png',
          description: 'View platform analytics and statistics',
          path: '/admin/analytics'
        },
        {
          title: 'Financial System',
          icon: '/icons/dash/financial.png',
          description: 'Manage payments and financial records',
          path: '/admin/financial'
        },
        {
          title: 'Master Database',
          icon: '/icons/dash/database.jpg',
          description: 'Manage study materials and resources',
          path: '/admin/content'
        },
        {
          title: 'Technical Board',
          icon: '/icons/dash/tech_board.png',
          description: 'Manage Technical Operations',
          path: '/admin/tech-board'
        },
        {
          title: 'Manage Meetings',
          icon: '/icons/dash/all_meetings.png',
          description: 'Manage all types of meetings and schedules',
          path: '/admin/meetings'
        }
      ]
    },
    contentCreation: {
      title: 'Content Creation & Marketing',
      items: [
        {
          title: 'Flowcharts',
          icon: '/icons/dash/flowchart.png',
          description: 'Visualize and manage medical management',
          path: '/admin/content-creation/flowchart'
        },
        {
          title: 'Assignment Bank',
          icon: '/icons/dash/assignment_create.png',
          description: 'Manage and create assignments',
          path: '/admin/content-creation/assignments'
        },
        {
          title: 'Marketing',
          icon: '/icons/dash/marketing.png',
          description: 'Manage marketing campaigns and content',
          path: '/admin/content-creation/marketing'
        }
      ]
    },
    mentorship: {
      title: 'Mentorship Programs',
      items: [
        {
          title: 'Step 1 Mentorship',
          icon: '/icons/dash/one.png',
          description: 'Manage Step 1 mentorship assignments and progress',
          path: '/admin/mentorship/step1',
          onClick: () => handleMentorshipNavigation('/admin/mentorship/step1')
        },
        {
          title: 'Step 2 Mentorship',
          icon: '/icons/dash/two.png',
          description: 'Manage Step 2 mentorship assignments and progress',
          path: '/admin/mentorship/step2',
          onClick: () => handleMentorshipNavigation('/admin/mentorship/step2')
        },
        {
          title: 'Mentoring',
          icon: '/icons/dash/Mentorship.jpg',
          description: 'Manage The Mentorship Program',
          path: '/admin/mentorship',
          onClick: () => handleMentorshipNavigation('/admin/mentorship')
        }
      ]
    },
    study: {
      title: 'Libraries',
      items: [
        {
          title: 'Step 1 Library',
          icon: '/icons/dash/step_1.png',
          description: 'Access Step 1 study materials and resources',
          path: '/admin/step1-library'
        },
        {
          title: 'Step 2 Library',
          icon: '/icons/dash/step_2.png',
          description: 'Access Step 2 study materials and resources',
          path: '/admin/step2-library'
        }
      ]
    },
    personal: {
      title: 'Personal Tools',
      items: [
        {
          title: 'My Bookmarks',
          icon: '/icons/dash/bookmark.png',
          description: 'Manage your saved items and resources',
          path: '/admin/bookmarks'
        },
        {
          title: 'My Notes',
          icon: '/icons/dash/notes.jpg',
          description: 'Create and manage your study notes',
          path: '/admin/notes'
        },
        {
          title: 'My Flashcards',
          icon: '/icons/dash/flashcards.png',
          description: 'Create and study with flashcards',
          path: '/admin/flashcards',
          locked: false
        },
        {
          title: 'Settings',
          icon: '/icons/dash/settings.png',
          description: 'Manage your account settings and preferences',
          path: '/admin/settings'
        },
        {
          title: 'Labs',
          icon: '/icons/dash/labs.png',
          description: 'Reference ranges for Blood, Serum, CSF, and Urine',
          path: '/admin/labs'
        }
      ]
    },
    community: {
      title: 'TEMP-U Community',
      items: [
        {
          title: 'Taking Notes',
          icon: '/icons/dash/external_notes.png',
          description: 'Explain Taking Notes',
          path: '/admin/community/notes'
        },
        {
          title: 'Guide',
          icon: '/icons/dash/guide.png',
          description: 'Manage USMLE study guides',
          path: '/admin/community/guide'
        },
        {
          title: 'Take a Break',
          icon: '/icons/dash/take_break.png',
          description: 'Breaktime content 😴',
          path: '/admin/community/break'
        },
        {
          title: 'Events',
          icon: '/icons/dash/events.png',
          description: 'Virtual meetings, lives and podcasts',
          path: '/admin/community/events'
        },
        {
          title: 'Roadmaps',
          icon: '/icons/dash/roadmap.png',
          description: 'Journey through the USMLE',
          path: '/admin/community/roadmaps'
        },
        {
          title: 'Content Updates',
          icon: '/icons/dash/updates.png',
          description: 'Latest platform updates',
          path: '/admin/community/updates'
        },
        {
          title: 'Clinical Rotations',
          icon: '/icons/dash/med_student.png',
          description: 'Manage rotation resources',
          path: '/admin/community/rotations'
        }
      ]
    },
    blog: {
      title: 'Social',
      items: [
        {
          title: 'Articles',
          icon: '/icons/dash/article.png',
          description: 'Manage articles',
          path: '/admin/blog/articles'
        },
        {
          title: 'Q & A',
          icon: '/icons/dash/qa.png',
          description: 'Manage questions and answers',
          path: '/admin/blog/qa'
        },
        {
          title: 'User Experiences',
          icon: '/icons/dash/experiences.png',
          description: 'Share and learn from other students',
          path: '/admin/community/experiences'
        },
        {
          title: 'Discussions',
          icon: '/icons/dash/discussion.png',
          description: 'Manage community discussions',
          path: '/admin/blog/discussions'
        }
      ]
    },
    extras: {
      title: 'TEMP-U Extras',
      items: [
        {
          title: 'Gallery',
          icon: '/icons/dash/gallery.png',
          description: 'Visual resources',
          path: '/admin/extras/gallery'
        },
        {
          title: 'Clinical Library',
          icon: '/icons/dash/clinical.png',
          description: 'Clinical skills resources',
          path: '/admin/extras/clinical'
        },
        {
          title: 'Research Library',
          icon: '/icons/dash/laboratory.png',
          description: 'Research resources',
          path: '/admin/extras/research'
        },
        {
          title: 'OET',
          icon: '/icons/dash/oet.png',
          description: 'OET resources',
          path: '/admin/extras/oet'
        }
      ]
    },
    feedback: {
      title: 'User Feedback',
      items: [
        {
          title: 'Feedback Management',
          icon: '/icons/dash/feedback.jpg',
          description: 'View and manage user feedback',
          path: '/admin/feedback'
        },
        {
          title: 'Review Management',
          icon: '/icons/dash/review_management.png',
          description: 'Manage and moderate user reviews',
          path: '/admin/review-management'
        },
        {
          title: 'Support Requests',
          icon: '/icons/dash/support.png',
          description: 'Handle user support tickets',
          path: '/admin/support'
        },
        {
          title: 'Reviews',
          icon: '/icons/dash/reviews.png',
          description: 'View and write reviews',
          path: '/admin/reviews'
        }
      ]
    }
  };

  // # Apply stagger animation to sections
  useEffect(() => {
    const sections = document.querySelectorAll('.admn_dash_section');
    sections.forEach((section, index) => {
      section.style.animationDelay = `${0.2 * index}s`;
    });
  }, []);

  // # Track profile data access with optimization
  useEffect(() => {
    let isMounted = true;
    
    if (!userProfile) return;
    
    const now = Date.now();
    // # Skip logging if last log was within 5 seconds
    if (now - lastProfileAccessRef.current.timestamp < 5000 && lastProfileAccessRef.current.logged) {
      return;
    }
    
    const startTime = performance.now();
    if (isMounted) {
      console.log('Admin Dashboard - Accessing user profile data:', {
        operation: 'get_display_name',
        timestamp: new Date().toISOString(),
        hasProfile: true,
        timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });
      
      // # Update last access ref
      lastProfileAccessRef.current = {
        timestamp: now,
        logged: true
      };
    }
    
    return () => {
      isMounted = false;
    };
  }, [userProfile]); // Only log when userProfile changes

  // # Update admin name display
  const getDisplayName = React.useMemo(() => {
    let name = '';
    let username = '';

    // Get name
    if (userProfile?.lastName) {
      name = `Dr. ${userProfile.lastName}`;
    } else if (userProfile?.firstName) {
      name = `Dr. ${userProfile.firstName}`;
    } else {
      name = `Dr. ${user?.email?.split('@')[0] || 'Admin'}`;
    }

    // Get username
    if (userProfile?.username) {
      username = `@${userProfile.username}`;
    }

    return { name, username };
  }, [userProfile, user]);

  // # Handle sign out
  const handleSignOut = async () => {
    try {
      const { signOut } = useAuthStore.getState(); // Get signOut from store
      await signOut(); // Use the store's signOut method
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    }
  };

  // Add the same helper function and update the img src similarly
  const getProfilePhotoUrl = (userProfile) => {
    try {
      if (!userProfile?.photoURL) return '/icons/pfp.png';
      if (userProfile.photoURL === '') return '/icons/pfp.png';
      
      // Check if it's a full URL (from Storage)
      if (userProfile.photoURL.startsWith('http')) {
        return userProfile.photoURL;
      }
      
      // If it's just a filename, assume it's the default
      return '/icons/pfp.png';
    } catch (error) {
      console.error('Error getting profile photo:', error);
      return '/icons/pfp.png';
    }
  };

  // # Close verification modal
  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
  };

  return (
    <div className={`admn_dash_dashboard-container ${isDarkMode ? 'admn_dash_dark-mode' : ''}`}>
      {/* # Pass theme props to header */}
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      {/* Cover Section */}
      <div className="admn_dash_cover-section">
        <img 
          src="/icons/cover_ocean.jpg" 
          alt="Cover" 
          className="admn_dash_cover-image"
        />
      </div>

      {/* Profile Section */}
      <div className="admn_dash_profile-section">
        <div 
          className="admn_dash_profile-image-container"
          onClick={() => navigate('/admin/profile')}
        >
          <img 
            src={getProfilePhotoUrl(userProfile)}
            alt="Profile" 
            className="admn_dash_profile-image"
          />
        </div>
        <div className="admn_dash_profile-info">
          <h2 className="admn_dash_profile-name">
            {getDisplayName.name}
          </h2>
          <div className="admn_dash-admin-username">
            {getDisplayName.username}
          </div>
        </div>
      </div>

      {/* Enhanced Dashboard Sections with Carousel */}
      <AdminDashboardUX sections={dashboardSections} isDarkMode={isDarkMode} />

      {/* Verification Modal */}
      {showVerificationModal && !emailVerified && (
        <VerificationModal onClose={handleCloseVerificationModal} />
      )}

      {/* Footer */}
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminDashboard;
